/* ============================================================
   Customer Video Reviews  (<customer-video-reviews>)
   - Paged horizontal grid carousel (arrows + native swipe), with
     circular looping: past the last page wraps to the first and vice-versa.
   - Uploaded (Shopify-hosted) clips: native <video>, lazy-loaded, and
     muted-autoplay only for the videos actually visible in the carousel
     (recomputed on every scroll/resize), so the page stays light and
     paging reliably starts/stops the right videos.
   - One sound at a time: unmuting a video mutes all the others.
   - YouTube / Vimeo: click-to-load facade (no iframe until tapped).
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('customer-video-reviews')) return;

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  class CustomerVideoReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cvr-track]');
      this.prevBtn = this.querySelector('[data-cvr-prev]');
      this.nextBtn = this.querySelector('[data-cvr-next]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-cvr-card]'));
      this.autoplay = this.getAttribute('data-autoplay') === 'true';
      this.previewSeconds = parseInt(this.getAttribute('data-preview'), 10) || 0;
      this.unmuteOnClick = this.getAttribute('data-unmute-click') !== 'false';
      this.activeMedia = null;   // the one video currently expanded (full + controls)
      this.activeVideo = null;
      this.unmuted = null;       // the one video the user has unmuted
      this.sectionInView = true; // updated by the section observer

      if (!this.track) return;

      this.bindArrows();
      this.bindCards();
      this.observeSection();

      this._refresh = debounce(this.refresh.bind(this), 100);
      this.track.addEventListener('scroll', this._refresh, { passive: true });
      window.addEventListener('resize', this._refresh);
      this.bindSwipeLoop();

      this.refresh();
    }

    disconnectedCallback() {
      if (this.sectionObserver) this.sectionObserver.disconnect();
      window.removeEventListener('resize', this._refresh);
    }

    refresh() {
      this.updateArrows();
      this.updatePlayback();
    }

    /* ---------- Arrows / circular paging ---------- */
    bindArrows() {
      var self = this;
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.page(-1); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.page(1); });
    }

    page(dir) {
      var t = this.track;
      var max = t.scrollWidth - t.clientWidth;
      var atStart = t.scrollLeft <= 2;
      var atEnd = t.scrollLeft >= max - 2;

      if (dir > 0 && atEnd) {
        t.scrollTo({ left: 0, behavior: 'smooth' });          // loop to start
      } else if (dir < 0 && atStart) {
        t.scrollTo({ left: max, behavior: 'smooth' });        // loop to end
      } else {
        t.scrollBy({ left: t.clientWidth * dir, behavior: 'smooth' });
      }
      // Re-evaluate once the smooth scroll has settled (belt and suspenders;
      // the scroll listener also fires during the animation).
      var self = this;
      clearTimeout(this._settle);
      this._settle = setTimeout(function () { self.refresh(); }, 500);
    }

    // Mobile: let a swipe past the last/first item wrap around, like the arrows.
    bindSwipeLoop() {
      var self = this, t = this.track;
      var x0 = 0, y0 = 0, atEnd0 = false, atStart0 = false;
      t.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
        var max = t.scrollWidth - t.clientWidth;
        atEnd0 = t.scrollLeft >= max - 2;
        atStart0 = t.scrollLeft <= 2;
      }, { passive: true });
      t.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy)) return; // not a clear horizontal swipe
        var max = t.scrollWidth - t.clientWidth;
        if (max <= 0) return;
        if (atEnd0 && t.scrollLeft >= max - 2 && dx < 0) {
          t.scrollTo({ left: 0, behavior: 'smooth' });
          clearTimeout(self._settle);
          self._settle = setTimeout(function () { self.refresh(); }, 500);
        } else if (atStart0 && t.scrollLeft <= 2 && dx > 0) {
          t.scrollTo({ left: max, behavior: 'smooth' });
          clearTimeout(self._settle);
          self._settle = setTimeout(function () { self.refresh(); }, 500);
        }
      }, { passive: true });
    }

    updateArrows() {
      if (!this.track) return;
      // With looping, both arrows stay available whenever there's more than one page.
      var fits = this.track.scrollWidth <= this.track.clientWidth + 2;
      if (this.prevBtn) this.prevBtn.hidden = fits;
      if (this.nextBtn) this.nextBtn.hidden = fits;
    }

    /* ---------- Play only what's visible (deterministic) ---------- */
    updatePlayback() {
      var self = this;
      var inView = this.sectionInView;
      var trackRect = this.track.getBoundingClientRect();

      this.cards.forEach(function (card) {
        var v = card._cvrVideo;
        if (!v) return;
        var media = card.querySelector('[data-cvr-media]');

        var visible = false;
        if (inView) {
          var r = card.getBoundingClientRect();
          var overlap = Math.min(r.right, trackRect.right) - Math.max(r.left, trackRect.left);
          visible = r.width > 0 && (overlap / r.width) > 0.6;
        }

        if (visible) {
          if (self.autoplay) {
            if (v !== self.unmuted) v.muted = true;
            if (v.paused) self.playVideo(v, media);
          }
        } else {
          if (!v.paused) v.pause();
          if (v === self.unmuted) self.unmuted = null;
          if (!v.muted) v.muted = true;
          if (media) media.classList.remove('is-unmuted');
        }
      });
    }

    observeSection() {
      var self = this;
      if (!('IntersectionObserver' in window)) {
        this.sectionInView = true;
        return;
      }
      this.sectionObserver = new IntersectionObserver(function (entries) {
        self.sectionInView = entries[0].isIntersecting;
        self.refresh();
      }, { threshold: 0 });
      this.sectionObserver.observe(this);
    }

    /* ---------- Cards / controls ---------- */
    bindCards() {
      var self = this;
      this.cards.forEach(function (card) {
        var media = card.querySelector('[data-cvr-media]');
        var type = media ? media.getAttribute('data-cvr-type') : 'hosted';
        var playBtn = card.querySelector('[data-cvr-play]');
        var soundBtn = card.querySelector('[data-cvr-sound]');

        var newtabBtn = card.querySelector('[data-cvr-newtab]');

        if (type === 'youtube' || type === 'vimeo') {
          var loadEmbed = function (e) {
            if (e) e.stopPropagation();
            self.loadEmbed(media);
          };
          if (media) media.addEventListener('click', loadEmbed);
          if (playBtn) playBtn.addEventListener('click', loadEmbed);
          if (newtabBtn) newtabBtn.addEventListener('click', function (e) {
            e.stopPropagation();
            self.openNewTab(media);
          });
          return;
        }

        var video = card.querySelector('[data-cvr-video]');
        if (!video) return;
        card._cvrVideo = video;

        video.addEventListener('playing', function () {
          media.classList.add('is-playing');
          self.hideSpinner(media, video);
        });
        // Clearing on the first frame means a video that can't actually start (e.g. the
        // browser limits how many play at once) shows its poster instead of a stuck spinner.
        video.addEventListener('loadeddata', function () { self.hideSpinner(media, video); });
        video.addEventListener('canplay', function () { self.hideSpinner(media, video); });
        video.addEventListener('error', function () { self.hideSpinner(media, video); });
        video.addEventListener('waiting', function () { self.showSpinner(media, video); });
        video.addEventListener('pause', function () {
          self.hideSpinner(media, video);
          if (!self.autoplay) media.classList.remove('is-playing');
        });

        // Preview loop: while not "revealed" (full), loop only the first N seconds so the
        // browser keeps fetching just the start of the clip instead of the whole file.
        video.addEventListener('timeupdate', function () {
          if (self.previewSeconds > 0 &&
              !media.classList.contains('is-full') &&
              video.currentTime >= self.previewSeconds) {
            try { video.currentTime = 0; } catch (e) {}
          }
        });

        // Click a video to activate it: load + loop the full clip and reveal the
        // unmute + open-in-new-tab icons. Clicking a different video reverts this one.
        // No hover behavior - everything is click/tap driven for smoothness.
        var activate = function (e) {
          if (e) e.stopPropagation();
          self.activate(video, media);
        };
        if (media) media.addEventListener('click', activate);
        if (playBtn) playBtn.addEventListener('click', activate);
        if (soundBtn) soundBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          self.toggleSound(video, media);
        });
        if (newtabBtn) newtabBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          self.openNewTab(media);
        });
      });
    }

    // Set the source directly (no <source> + load(), which can abort a pending play()).
    ensureSrc(video) {
      if (video.dataset.cvrLoaded === 'true') return;
      var src = video.getAttribute('data-src');
      if (src) video.src = src;
      video.dataset.cvrLoaded = 'true';
    }

    // Play + show the buffering spinner until the video actually starts.
    playVideo(video, media) {
      this.ensureSrc(video);
      if (media && !media.classList.contains('is-playing')) this.showSpinner(media, video);
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    }

    // Spinner that can never get stuck: it auto-clears after a few seconds (then the
    // poster shows), so a stalled or decode-limited video won't spin forever.
    showSpinner(media, video) {
      if (!media) return;
      media.classList.add('is-loading');
      clearTimeout(video._spin);
      video._spin = setTimeout(function () { media.classList.remove('is-loading'); }, 7000);
    }
    hideSpinner(media, video) {
      if (!media) return;
      media.classList.remove('is-loading');
      clearTimeout(video._spin);
    }

    // Make this the single active video; revert whatever was active before.
    activate(video, media) {
      if (this.activeMedia && this.activeMedia !== media) {
        this.revertPreview(this.activeVideo, this.activeMedia);
      }
      this.activeMedia = media;
      this.activeVideo = video;
      this.revealFull(video, media);
      // Optionally turn the sound on automatically (this is a user click, so unmuting is
      // allowed). The speaker icon stays, so the visitor can mute it again if they prefer.
      if (this.unmuteOnClick) this.unmute(video, media);
    }

    // Load + loop the full clip and reveal the unmute + open-in-new-tab icons.
    revealFull(video, media) {
      media.classList.add('is-full');
      this.ensureSrc(video);
      if (video.paused) this.playVideo(video, media);
    }

    // Back to the muted, first-few-seconds preview with no icons.
    revertPreview(video, media) {
      media.classList.remove('is-full');
      video.muted = true;
      if (this.unmuted === video) this.unmuted = null;
      media.classList.remove('is-unmuted');
      if (this.previewSeconds > 0 && video.currentTime > this.previewSeconds) {
        try { video.currentTime = 0; } catch (e) {}
      }
    }

    // Unmute this video and mute every other one (one sound at a time).
    unmute(video, media) {
      this.muteOthers(video);
      this.unmuted = video;
      video.muted = false;
      media.classList.add('is-unmuted');
      if (video.paused) this.playVideo(video, media);
    }

    toggleSound(video, media) {
      if (video.muted) {
        this.unmute(video, media);
      } else {
        video.muted = true;
        if (this.unmuted === video) this.unmuted = null;
        media.classList.remove('is-unmuted');
      }
    }

    // Open the clip in a new browser tab; the browser loads + plays it natively.
    openNewTab(media) {
      var url = media && media.getAttribute('data-open-url');
      if (url) window.open(url, '_blank', 'noopener');
    }

    muteOthers(except) {
      this.cards.forEach(function (card) {
        var v = card._cvrVideo;
        if (v && v !== except) {
          v.muted = true;
          var m = card.querySelector('[data-cvr-media]');
          if (m) m.classList.remove('is-unmuted');
        }
      });
    }

    /* ---------- YouTube / Vimeo facade ---------- */
    loadEmbed(media) {
      if (!media || media.dataset.cvrEmbedded === 'true') return;
      var url = media.getAttribute('data-embed');
      if (!url) return;
      var iframe = document.createElement('iframe');
      iframe.className = 'cvr__embed';
      iframe.setAttribute('src', url);
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', media.getAttribute('data-title') || 'Customer video');
      media.appendChild(iframe);
      media.classList.add('is-playing');
      media.dataset.cvrEmbedded = 'true';
    }
  }

  customElements.define('customer-video-reviews', CustomerVideoReviews);
})();
